import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import toast from 'react-hot-toast';
import KeyManager from './KeyManager';
import IntegrationHealth from '../components/settings/IntegrationHealth';
import GoLiveChecklist from '../components/settings/GoLiveChecklist';
import PublicLinkBase from '../components/settings/PublicLinkBase';
import RepProfileCard from '../components/settings/RepProfileCard';

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ notifications: true, theme: 'light', lead_alerts: true, mining_alerts: true, email_notifications: false });
  const [, setApiKeys] = useState({});
  const [pipeline, setPipeline] = useState({ auto_enrich_enabled: false, deep_verify_emails: false, auto_drip_enabled: false, drip_min_score: 75, drip_min_accuracy: 50, ai_outreach_enabled: true, max_drip_per_scan: 10 });
  const [dripQueue, setDripQueue] = useState({ queue: [], stats: {} });
  const [briefing, setBriefing] = useState({ slack_enabled: false, slack_webhook_url: '', slack_channel_name: '', email_enabled: false, email_provider: 'sendgrid', email_recipients: [], briefing_time: '06:00', include_low_score: false, max_leads_in_briefing: 10 });
  const [briefingLogs, setBriefingLogs] = useState([]);
  const [auditLog, setAuditLog] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profile');
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [briefingSaving, setBriefingSaving] = useState(false);
  const [testingSending, setTestingSending] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/settings'),
      user?.is_super ? api.get('/api/settings/api-keys') : Promise.resolve({ data: { api_keys: {} } }),
      api.get('/api/pipeline/settings'),
      api.get('/api/pipeline/queue'),
      api.get('/api/briefing/config').catch(() => ({ data: { config: {} } })),
      api.get('/api/briefing/logs').catch(() => ({ data: { logs: [] } })),
    ]).then(([sRes, kRes, pRes, qRes, bRes, blRes]) => {
      if (sRes.data) setSettings(prev => ({ ...prev, ...sRes.data }));
      setApiKeys(kRes.data.api_keys || {});
      setPipeline(prev => ({ ...prev, ...pRes.data }));
      setDripQueue(qRes.data || { queue: [], stats: {} });
      if (bRes.data?.config) setBriefing(prev => ({ ...prev, ...bRes.data.config }));
      if (blRes.data?.logs) setBriefingLogs(blRes.data.logs);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const r = await api.get('/api/admin/audit-log?limit=200');
      setAuditLog(r.data.entries || []);
    } catch { setAuditLog([]); }
    setAuditLoading(false);
  };
  useEffect(() => { if (tab === 'audit' && user?.is_super && auditLog === null) loadAudit(); }, [tab]); // eslint-disable-line

  const handleSave = async () => {
    try { await api.put('/api/settings', settings); toast.success('Settings saved!'); } catch { toast.error('Failed'); }
  };

  const handlePipelineSave = async () => {
    setPipelineSaving(true);
    try { await api.put('/api/pipeline/settings', pipeline); toast.success('Pipeline settings saved!'); }
    catch { toast.error('Failed to save pipeline settings'); }
    finally { setPipelineSaving(false); }
  };

  const handleRemoveFromQueue = async (leadId) => {
    try { await api.delete(`/api/pipeline/queue/${leadId}`); setDripQueue(prev => ({ ...prev, queue: prev.queue.filter(q => q.lead_id !== leadId) })); toast.success('Removed from queue'); }
    catch { toast.error('Failed'); }
  };

  const handleBriefingSave = async () => {
    setBriefingSaving(true);
    try {
      const res = await api.put('/api/briefing/config', briefing);
      if (res.data?.config) setBriefing(prev => ({ ...prev, ...res.data.config }));
      toast.success('Briefing configuration saved!');
    } catch { toast.error('Failed to save briefing configuration'); }
    finally { setBriefingSaving(false); }
  };

  const handleTestBriefing = async (type) => {
    setTestingSending(type);
    try {
      const res = await api.post('/api/briefing/test', { type });
      const r = res.data?.results || {};
      if (r.slack === true || r.email === true) toast.success(`Test ${type} briefing sent!`);
      else if (r.slack_error || r.email_error) toast.error(`Test failed: ${r.slack_error || r.email_error}`);
      else toast.success('Test briefing sent — check your channel/inbox');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send test'); }
    finally { setTestingSending(null); }
  };

  const addEmailRecipient = () => {
    const email = emailInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !briefing.email_recipients.includes(email)) {
      setBriefing(prev => ({ ...prev, email_recipients: [...prev.email_recipients, email] }));
      setEmailInput('');
    } else if (!email) { toast.error('Enter an email address'); }
    else { toast.error('Invalid or duplicate email'); }
  };

  const removeEmailRecipient = (email) => {
    setBriefing(prev => ({ ...prev, email_recipients: prev.email_recipients.filter(e => e !== email) }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div><h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Settings</h1><p className="text-text-muted text-sm mt-1">Manage account and platform configuration</p></div>
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {['profile', 'briefings', 'pipeline', 'notifications', 'api-keys', 'key-manager', 'audit', 'billing'].filter(t => !['key-manager', 'api-keys', 'audit'].includes(t) || user?.is_super).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-5 py-3 text-sm font-medium capitalize transition-all border-b-2 whitespace-nowrap ${tab === t ? 'text-primary border-primary' : 'text-text-muted border-transparent hover:text-text-primary'}`} data-testid={`settings-tab-${t}`}>{t === 'pipeline' ? 'Auto-Pipeline' : t === 'briefings' ? 'Morning Briefing' : t === 'api-keys' ? 'API Keys' : t === 'key-manager' ? 'Key Manager' : t === 'audit' ? 'Audit Trail' : t.replace('-', ' ')}</button>
        ))}
      </div>

      {tab === 'profile' && <RepProfileCard user={user} />}

      {tab === 'briefings' && (
        <div className="space-y-6 max-w-3xl" data-testid="briefing-settings">
          {/* Slack Configuration */}
          <div className="card-interactive p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#611f69] to-[#4a154b] flex items-center justify-center text-white"><i className="fa-brands fa-slack text-lg" /></div>
              <div className="flex-1">
                <h3 className="font-heading text-base font-semibold text-text-primary">Slack Morning Briefing</h3>
                <p className="text-xs text-text-muted">Send daily digest to your team's Slack channel</p>
              </div>
              <button onClick={() => setBriefing(p => ({ ...p, slack_enabled: !p.slack_enabled }))}
                className={`w-11 h-6 rounded-full transition-all duration-300 relative ${briefing.slack_enabled ? 'bg-primary' : 'bg-nardo-light'}`}
                data-testid="toggle-slack-briefing">
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${briefing.slack_enabled ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>
            {briefing.slack_enabled && (
              <div className="space-y-4 pt-2 border-t border-border">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2 mt-4">Webhook URL</label>
                  <input type="url" value={briefing.slack_webhook_url} onChange={e => setBriefing(p => ({ ...p, slack_webhook_url: e.target.value }))}
                    placeholder="https://hooks.slack.com/services/T00000/B00000/XXXX"
                    className="input-field text-sm font-mono w-full" data-testid="slack-webhook-url" />
                  <p className="text-[10px] text-text-muted mt-1">
                    <i className="fa-solid fa-circle-info mr-1" />
                    Create at: <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Slack Incoming Webhooks</a> → Add to your channel
                  </p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Channel Name (optional)</label>
                  <input type="text" value={briefing.slack_channel_name} onChange={e => setBriefing(p => ({ ...p, slack_channel_name: e.target.value }))}
                    placeholder="#solar-leads" className="input-field text-sm w-full" data-testid="slack-channel-name" />
                </div>
                <button onClick={() => handleTestBriefing('slack')} disabled={!briefing.slack_webhook_url || testingSending === 'slack'}
                  className="btn-secondary text-xs py-2 px-4" data-testid="test-slack-btn">
                  {testingSending === 'slack' ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Sending...</> : <><i className="fa-brands fa-slack mr-1.5" />Send Test to Slack</>}
                </button>
              </div>
            )}
          </div>

          {/* Email Configuration */}
          <div className="card-interactive p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center text-white"><i className="fa-solid fa-envelope text-lg" /></div>
              <div className="flex-1">
                <h3 className="font-heading text-base font-semibold text-text-primary">Email Morning Briefing</h3>
                <p className="text-xs text-text-muted">Email daily digest to your team every morning</p>
              </div>
              <button onClick={() => setBriefing(p => ({ ...p, email_enabled: !p.email_enabled }))}
                className={`w-11 h-6 rounded-full transition-all duration-300 relative ${briefing.email_enabled ? 'bg-primary' : 'bg-nardo-light'}`}
                data-testid="toggle-email-briefing">
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${briefing.email_enabled ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>
            {briefing.email_enabled && (
              <div className="space-y-4 pt-2 border-t border-border">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2 mt-4">Email Provider</label>
                  <div className="flex gap-2">
                    {['sendgrid', 'resend'].map(p => (
                      <button key={p} onClick={() => setBriefing(prev => ({ ...prev, email_provider: p }))}
                        className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all text-sm font-medium ${briefing.email_provider === p ? 'border-primary bg-primary/5 text-primary' : 'border-border bg-surface-alt text-text-muted hover:border-primary/30'}`}
                        data-testid={`provider-${p}`}>
                        <i className={`fa-solid ${p === 'sendgrid' ? 'fa-paper-plane' : 'fa-bolt'} mr-2`} />
                        {p === 'sendgrid' ? 'SendGrid' : 'Resend'}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-text-muted mt-2">
                    <i className="fa-solid fa-circle-info mr-1" />
                    {briefing.email_provider === 'sendgrid'
                      ? <>API key required in .env as SENDGRID_API_KEY · <a href="https://sendgrid.com/docs/ui/account-and-settings/api-keys/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Get key</a></>
                      : <>API key required in .env as RESEND_API_KEY · <a href="https://resend.com/docs/dashboard/api-keys/introduction" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Get key</a></>}
                  </p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Recipients</label>
                  <div className="flex gap-2">
                    <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmailRecipient())}
                      placeholder="closer@yourcompany.com" className="input-field text-sm flex-1" data-testid="email-recipient-input" />
                    <button onClick={addEmailRecipient} className="btn-primary text-xs px-4" data-testid="add-email-btn">
                      <i className="fa-solid fa-plus mr-1" />Add
                    </button>
                  </div>
                  {briefing.email_recipients.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {briefing.email_recipients.map(email => (
                        <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-alt border border-border text-xs font-medium text-text-secondary" data-testid={`recipient-${email}`}>
                          <i className="fa-solid fa-envelope text-[10px] text-text-muted" />{email}
                          <button onClick={() => removeEmailRecipient(email)} className="ml-1 text-destructive hover:text-destructive/80 transition-colors" data-testid={`remove-${email}`}>
                            <i className="fa-solid fa-xmark text-[10px]" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => handleTestBriefing('email')} disabled={briefing.email_recipients.length === 0 || testingSending === 'email'}
                  className="btn-secondary text-xs py-2 px-4" data-testid="test-email-btn">
                  {testingSending === 'email' ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Sending...</> : <><i className="fa-solid fa-envelope mr-1.5" />Send Test Email</>}
                </button>
              </div>
            )}
          </div>

          {/* Schedule & Options */}
          <div className="card-interactive p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white"><i className="fa-solid fa-clock" /></div>
              <div>
                <h3 className="font-heading text-base font-semibold text-text-primary">Schedule & Options</h3>
                <p className="text-xs text-text-muted">Configure when and what to include in briefings</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Briefing Time (UTC)</label>
                <input type="time" value={briefing.briefing_time} onChange={e => setBriefing(p => ({ ...p, briefing_time: e.target.value }))}
                  className="input-field text-sm w-full" data-testid="briefing-time" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Max Leads in Briefing</label>
                <input type="number" min="3" max="25" value={briefing.max_leads_in_briefing} onChange={e => setBriefing(p => ({ ...p, max_leads_in_briefing: parseInt(e.target.value) || 10 }))}
                  className="input-field text-sm w-24" data-testid="max-leads-briefing" />
              </div>
            </div>
            <div className="flex items-center justify-between py-4 mt-4 border-t border-border">
              <div><p className="text-sm font-medium text-text-primary">Include Low Score Leads</p><p className="text-xs text-text-muted mt-0.5">Include leads with score below 75 in the briefing</p></div>
              <button onClick={() => setBriefing(p => ({ ...p, include_low_score: !p.include_low_score }))}
                className={`w-11 h-6 rounded-full transition-all duration-300 relative ${briefing.include_low_score ? 'bg-primary' : 'bg-nardo-light'}`}
                data-testid="toggle-include-low-score">
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${briefing.include_low_score ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>
          </div>

          <button onClick={handleBriefingSave} disabled={briefingSaving} className="btn-primary text-sm" data-testid="save-briefing-btn">
            {briefingSaving ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Saving...</> : <><i className="fa-solid fa-check mr-2" />Save Briefing Settings</>}
          </button>

          {/* Briefing History */}
          {briefingLogs.length > 0 && (
            <div className="card-interactive p-6">
              <h3 className="font-heading text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                <i className="fa-solid fa-clock-rotate-left text-primary" /> Recent Briefings
              </h3>
              <div className="space-y-2">
                {briefingLogs.slice(0, 5).map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-surface-alt border border-border" data-testid={`briefing-log-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-text-muted font-mono">{log.digest_date}</span>
                      <div className="flex gap-2">
                        {log.slack_sent !== null && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded ${log.slack_sent ? 'bg-primary/10 text-primary' : 'bg-destructive-light text-destructive'}`}>
                            <i className="fa-brands fa-slack" />{log.slack_sent ? 'Sent' : 'Failed'}
                          </span>
                        )}
                        {log.email_sent !== null && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded ${log.email_sent ? 'bg-primary/10 text-primary' : 'bg-destructive-light text-destructive'}`}>
                            <i className="fa-solid fa-envelope" />{log.email_sent ? 'Sent' : 'Failed'}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] text-text-muted">{log.email_provider}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'pipeline' && (
        <div className="space-y-6 max-w-3xl">
          {/* Pipeline Controls */}
          <div className="card-interactive p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white"><i className="fa-solid fa-bolt" /></div>
              <div>
                <h3 className="font-heading text-base font-semibold text-text-primary">Auto-Enrichment Pipeline</h3>
                <p className="text-xs text-text-muted">Automatically enrich new leads with owner contacts when scanning</p>
              </div>
            </div>
            <div className="space-y-0">
              {[
                { k: 'auto_enrich_enabled', l: 'Auto-Enrich on Scan', d: 'Automatically run contact enrichment on every new lead from scanner', icon: 'fa-wand-magic-sparkles' },
                { k: 'deep_verify_emails', l: 'Deep Email Verification (SMTP)', d: 'Check if email mailbox actually exists — slower but much more accurate', icon: 'fa-shield-check' },
                { k: 'ai_outreach_enabled', l: 'AI Outreach Strategy', d: 'Auto-generate AI-powered personalized outreach plans for high-value leads', icon: 'fa-brain' },
                { k: 'auto_drip_enabled', l: 'Auto-Queue for Drip Campaign', d: 'Automatically queue high-scoring, verified leads for email outreach', icon: 'fa-paper-plane' },
              ].map(item => (
                <div key={item.k} className="flex items-center justify-between py-4 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    <i className={`fa-solid ${item.icon} w-5 text-center text-text-muted text-sm`} />
                    <div><p className="text-sm font-medium text-text-primary">{item.l}</p><p className="text-xs text-text-muted mt-0.5">{item.d}</p></div>
                  </div>
                  <button onClick={() => setPipeline(p => ({ ...p, [item.k]: !p[item.k] }))}
                    className={`w-11 h-6 rounded-full transition-all duration-300 relative ${pipeline[item.k] ? 'bg-primary' : 'bg-nardo-light'}`}
                    data-testid={`toggle-${item.k}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${pipeline[item.k] ? 'left-[22px]' : 'left-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Drip Campaign Thresholds */}
          {pipeline.auto_drip_enabled && (
            <div className="card-interactive p-6">
              <h3 className="font-heading text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                <i className="fa-solid fa-sliders text-primary" /> Drip Queue Thresholds
              </h3>
              <p className="text-xs text-text-muted mb-4">Only leads meeting BOTH thresholds will be auto-queued for outreach</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Min Lead Score</label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="50" max="95" value={pipeline.drip_min_score} onChange={e => setPipeline(p => ({ ...p, drip_min_score: parseInt(e.target.value) }))}
                      className="flex-1 h-2 bg-nardo-light rounded-full appearance-none cursor-pointer accent-primary" data-testid="drip-min-score" />
                    <span className="text-sm font-bold text-text-primary font-mono w-8 text-right">{pipeline.drip_min_score}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Min Accuracy Score</label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="20" max="90" value={pipeline.drip_min_accuracy} onChange={e => setPipeline(p => ({ ...p, drip_min_accuracy: parseInt(e.target.value) }))}
                      className="flex-1 h-2 bg-nardo-light rounded-full appearance-none cursor-pointer accent-primary" data-testid="drip-min-accuracy" />
                    <span className="text-sm font-bold text-text-primary font-mono w-8 text-right">{pipeline.drip_min_accuracy}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Max per Scan</label>
                  <input type="number" min="1" max="25" value={pipeline.max_drip_per_scan} onChange={e => setPipeline(p => ({ ...p, max_drip_per_scan: parseInt(e.target.value) || 10 }))}
                    className="input-field h-9 text-sm font-mono w-24" data-testid="drip-max-per-scan" />
                </div>
              </div>
            </div>
          )}

          <button onClick={handlePipelineSave} disabled={pipelineSaving} className="btn-primary text-sm" data-testid="save-pipeline-btn">
            {pipelineSaving ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Saving...</> : <><i className="fa-solid fa-check mr-2" />Save Pipeline Settings</>}
          </button>

          {/* Drip Queue Preview */}
          {pipeline.auto_drip_enabled && (
            <div className="card-interactive p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-sm font-semibold text-text-primary flex items-center gap-2">
                  <i className="fa-solid fa-list-check text-primary" /> Drip Queue
                </h3>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1 text-text-muted"><span className="w-2 h-2 rounded-full bg-amber-400" />{dripQueue.stats?.total_queued || 0} Queued</span>
                  <span className="flex items-center gap-1 text-text-muted"><span className="w-2 h-2 rounded-full bg-primary" />{dripQueue.stats?.sent || 0} Sent</span>
                </div>
              </div>
              {(!dripQueue.queue || dripQueue.queue.length === 0) ? (
                <p className="text-sm text-text-muted py-6 text-center">No leads queued yet. Run a scan with auto-enrichment enabled to populate.</p>
              ) : (
                <div className="space-y-2">
                  {dripQueue.queue.slice(0, 10).map((q, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-surface-alt border border-border" data-testid={`drip-queue-item-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary text-xs font-bold">{q.owner_name?.charAt(0) || '?'}</div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">{q.owner_name}</p>
                          <p className="text-[11px] text-text-muted font-mono">{q.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[10px] text-text-muted">Score: <span className="font-bold text-text-primary">{q.lead_score}</span></p>
                          <p className="text-[10px] text-text-muted">Accuracy: <span className={`font-bold ${q.accuracy_score >= 60 ? 'text-primary' : 'text-amber-600'}`}>{q.accuracy_score}%</span></p>
                        </div>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${q.status === 'queued' ? 'bg-amber-50 text-amber-700' : q.status === 'sent' ? 'bg-primary/10 text-primary' : 'bg-muted text-text-muted'}`}>{q.status}</span>
                        <button onClick={() => handleRemoveFromQueue(q.lead_id)} className="text-xs text-destructive hover:bg-destructive-light p-1 rounded" data-testid={`remove-drip-${i}`}>
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}



      {tab === 'notifications' && (
        <div className="card-interactive p-6 max-w-2xl">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-5">Notification Preferences</h3>
          <div className="space-y-0">
            {[{ k: 'notifications', l: 'Push Notifications', d: 'Receive in-app push notifications' }, { k: 'lead_alerts', l: 'Lead Alerts', d: 'Notified when new leads are captured' }, { k: 'mining_alerts', l: 'Scanner Alerts', d: 'Notified when mining jobs complete' }, { k: 'email_notifications', l: 'Email Digests', d: 'Daily email summary (requires SendGrid)' }].map(item => (
              <div key={item.k} className="flex items-center justify-between py-4 border-b border-border last:border-0">
                <div><p className="text-sm font-medium text-text-primary">{item.l}</p><p className="text-xs text-text-muted mt-0.5">{item.d}</p></div>
                <button onClick={() => setSettings(s => ({ ...s, [item.k]: !s[item.k] }))} className={`w-11 h-6 rounded-full transition-all duration-300 relative ${settings[item.k] ? 'bg-primary' : 'bg-nardo-light'}`} data-testid={`toggle-${item.k}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${settings[item.k] ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={handleSave} className="btn-primary text-sm mt-5" data-testid="save-settings-btn">Save Preferences</button>
        </div>
      )}

      {tab === 'api-keys' && user?.is_super && <div className="space-y-6"><GoLiveChecklist /><PublicLinkBase /><IntegrationHealth /></div>}

      {tab === 'key-manager' && user?.is_super && <KeyManager />}

      {/* Audit Trail — super admin only */}
      {tab === 'audit' && user?.is_super && (
        <div className="bg-surface rounded-2xl border border-border overflow-hidden" data-testid="audit-trail-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <p className="text-sm font-semibold text-text-primary"><i className="fa-solid fa-shield-halved text-primary mr-2" />Audit Trail</p>
              <p className="text-xs text-text-muted mt-0.5">Sign-ins and every change made, newest first</p>
            </div>
            <button onClick={loadAudit} disabled={auditLoading} className="btn-secondary text-xs" data-testid="refresh-audit-btn">
              {auditLoading ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5" /> : <i className="fa-solid fa-rotate mr-1.5" />}Refresh
            </button>
          </div>
          {!auditLog ? (
            <div className="py-14 text-center text-text-muted text-sm"><i className="fa-solid fa-circle-notch fa-spin text-xl block mb-2" />Loading…</div>
          ) : auditLog.length === 0 ? (
            <div className="py-14 text-center text-text-muted text-sm">No audit events yet</div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0"><tr className="bg-surface-alt text-[10px] font-bold uppercase tracking-widest text-text-muted">
                  <th className="px-4 py-2.5 text-left">Time</th><th className="px-4 py-2.5 text-left">User</th><th className="px-4 py-2.5 text-left">Event</th><th className="px-4 py-2.5 text-left">Detail</th><th className="px-4 py-2.5 text-left">IP</th><th className="px-4 py-2.5 text-left">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-border/60">
                  {auditLog.map((e, i) => (
                    <tr key={e.id || i} className="hover:bg-surface-alt/40" data-testid="audit-row">
                      <td className="px-4 py-2.5 font-mono whitespace-nowrap text-text-secondary">{e.ts ? new Date(e.ts).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2.5 font-medium text-text-primary">{e.user_email || 'anonymous'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${e.type === 'login' ? 'bg-emerald-100 text-emerald-700' : e.type === 'login_failed' ? 'bg-red-100 text-red-700' : 'bg-primary/10 text-primary'}`}>{e.type === 'action' ? e.method : e.type.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-text-secondary">{e.type === 'action' ? e.path : (e.role ? `role: ${e.role}${e.is_super ? ' (super)' : ''}` : '—')}</td>
                      <td className="px-4 py-2.5 font-mono text-text-muted">{e.ip || '—'}</td>
                      <td className="px-4 py-2.5">{e.status ? <span className={e.status < 400 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{e.status}</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'billing' && (
        <div className="card-interactive p-6 max-w-2xl">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-5">Billing & Credits</h3>
          <div className="p-5 rounded-xl bg-primary/5 border border-primary/15 mb-4">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-1">Current Balance</p>
            <p className="font-heading text-3xl font-bold text-text-primary tabular-nums">{user?.credits || 0} <span className="text-sm font-normal text-text-muted">credits</span></p>
          </div>
          <p className="text-xs text-text-muted">Credits are consumed when running scanners and generating proposals. Contact admin to add more credits.</p>
        </div>
      )}
    </div>
  );
}
